// Demo VAT codes, as ledger.vat_codes and its changes (db/023_vat_codes.sql):
// the same rules, against Irish demo books that have filed to 31 August 2026.
import type { LedgerSource, NewVatCode, TaxCode, VatCode, VatCodeList, VatCodePatch } from './source';

type Part = Pick<LedgerSource, 'vatCodes' | 'createVatCode' | 'updateVatCode' | 'setVatCodeActive' | 'deleteVatCode' | 'setVatRate' | 'removeVatRate'>;
type Kind = VatCode['kind'];
interface Rate { rate: string; from: string; to: string | null }
interface Stored { code: string; name: string; kind: Kind; applies_to: VatCode['applies_to']; is_active: boolean; rates: Rate[]; boxes: string[]; used_until: string | null; on_documents: boolean }

const delay = <T,>(v: T, ms = 60): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (m: string) => Promise.reject(new Error(m));
const DEMO_TODAY = '2026-10-12';
const LAST_FILED = '2026-08-31';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (d: string) => `${Number(d.slice(8))} ${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
const dayAfter = (d: string) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + 1); return x.toISOString().slice(0, 10); };

export const IE_BOXES: VatCodeList['boxes'] = [
  { box: 'T1', measure: 'tax', side: 'output', label: 'T1: VAT on sales' },
  { box: 'T2', measure: 'tax', side: 'input', label: 'T2: VAT on purchases' },
  { box: 'E1', measure: 'net', side: 'output', label: 'E1: goods to other EU countries' },
  { box: 'E2', measure: 'net', side: 'input', label: 'E2: goods from other EU countries' },
  { box: 'ES1', measure: 'net', side: 'output', label: 'ES1: services to other EU countries' },
  { box: 'ES2', measure: 'net', side: 'input', label: 'ES2: services from other EU countries' },
  { box: 'PA1', measure: 'net', side: 'input', label: 'PA1: imports under postponed accounting' },
];

const one = (rate: string): Rate[] => [{ rate, from: '2000-01-01', to: null }];
function seed(): Stored[] {
  const c = (code: string, name: string, kind: Kind, rate: string, boxes: string[], applies_to: Stored['applies_to'] = 'both', used: string | null = null): Stored =>
    ({ code, name, kind, applies_to, is_active: true, rates: one(rate), boxes, used_until: used, on_documents: used !== null });
  return [
    c('S23', 'Standard 23%', 'standard', '23.00', ['T1', 'T2'], 'both', '2026-10-09'),
    c('R135', 'Reduced 13.5%', 'reduced', '13.50', ['T1', 'T2'], 'both', '2026-09-30'),
    c('R9', 'Second reduced 9%', 'second_reduced', '9.00', ['T1', 'T2'], 'both', '2026-08-14'),
    c('L48', 'Livestock 4.8%', 'livestock', '4.80', ['T1', 'T2']),
    c('Z', 'Zero rated', 'zero', '0.00', [], 'both', '2026-10-02'),
    c('EX', 'Exempt', 'exempt', '0.00', [], 'both', '2026-09-12'),
    c('RCT', 'Reverse charge — construction (RCT) 13.5%', 'reverse_charge', '13.50', ['T1', 'T2']),
    c('EUGS', 'EU goods supplied (zero)', 'intra_eu_goods', '0.00', ['E1'], 'sales'),
    c('EUSS', 'EU services supplied (RC)', 'intra_eu_services', '0.00', ['ES1'], 'sales'),
    c('EUGA', 'EU goods acquired 23%', 'intra_eu_goods', '23.00', ['E2', 'T1', 'T2'], 'purchases'),
    c('EUSR', 'EU services received 23%', 'intra_eu_services', '23.00', ['ES2', 'T1', 'T2'], 'purchases', '2026-09-01'),
    c('PVA', 'Postponed accounting imports 23%', 'import_postponed', '23.00', ['PA1', 'T1', 'T2'], 'purchases'),
  ];
}

let store: Stored[] = seed();
export function resetDemoVat() { store = seed(); }

function rateOn(s: Stored, on: string): string | null {
  return s.rates.find((r) => on >= r.from && (r.to === null || on < r.to))?.rate ?? null;
}

/** For the demo invoice sums and pickers: the rate in force on a day. */
export function demoVatRate(code: string | undefined, on = DEMO_TODAY): string {
  const s = store.find((x) => x.code === code);
  return (s && rateOn(s, on)) ?? '0';
}

/** What GET /tax-codes gives: the codes switched on, at the rate on a day. */
export function demoTaxCodes(on = DEMO_TODAY): TaxCode[] {
  return store.filter((s) => s.is_active).sort((a, b) => a.code.localeCompare(b.code)).map((s) => ({
    code: s.code, name: s.name, rate: Number(rateOn(s, on) ?? '0').toFixed(2), applies_to: s.applies_to,
    ...(isRc(s.kind) ? { is_reverse_charge: true } : {}), return_boxes: s.boxes,
  }));
}

const isRc = (k: Kind) => ['reverse_charge', 'intra_eu_goods', 'intra_eu_services', 'import_postponed'].includes(k);

function view(on = DEMO_TODAY, extra: Partial<VatCodeList> = {}): VatCodeList {
  const codes: VatCode[] = [...store].sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.code.localeCompare(b.code)).map((s) => {
    const next = s.rates.filter((r) => r.from > on).sort((a, b) => a.from.localeCompare(b.from))[0];
    return {
      code: s.code, name: s.name, kind: s.kind, is_active: s.is_active, applies_to: s.applies_to, is_reverse_charge: isRc(s.kind),
      rate: rateOn(s, on), boxes: s.boxes, in_use: s.on_documents || s.used_until !== null, used_until: s.used_until, used_by: {},
      rates: [...s.rates].sort((a, b) => b.from.localeCompare(a.from))
        .map((r) => ({ ...r, used: s.used_until !== null && s.used_until >= r.from })),
      next_change: next ? { rate: next.rate, from: next.from } : null,
    };
  });
  return { return_type: 'IE_VAT3', last_filed_to: LAST_FILED, boxes: IE_BOXES, codes, ...extra };
}

function percent(raw: string): string | Error {
  const v = Number(String(raw).replace('%', '').trim());
  if (String(raw).trim() === '' || Number.isNaN(v)) return new Error('The rate is a percentage, such as 13.5');
  if (v < 0 || v >= 100) return new Error('The rate is a percentage from 0 to under 100');
  return v.toFixed(2).replace(/(\.\d\d)$/, '$1');
}

function defaultBoxes(kind: Kind, applies: string, rate: string): string[] {
  if (Number(rate) > 0 && ['standard', 'reduced', 'second_reduced', 'livestock'].includes(kind)) {
    return [applies !== 'purchases' && 'T1', applies !== 'sales' && 'T2'].filter(Boolean) as string[];
  }
  return [];
}

function checkBoxes(boxes: string[] | undefined): Error | null {
  const bad = boxes?.find((b) => !IE_BOXES.some((x) => x.box === b));
  return bad ? new Error(`${bad} isn’t a box on the VAT3`) : null;
}

export const demoVat: Part = {
  vatCodes: (on) => delay(view(on)),
  createVatCode: (input: NewVatCode) => {
    const code = (input.code ?? '').trim().toUpperCase();
    if (!/^[A-Z0-9]{1,10}$/.test(code)) return fail('A VAT code is 1 to 10 letters or digits, such as S23 or R135');
    if (store.some((s) => s.code === code)) return fail(`There’s already a VAT code ${code}`);
    if (!input.name?.trim()) return fail('The VAT code needs a name');
    const rate = percent(input.rate);
    if (rate instanceof Error) return fail(rate.message);
    const kind: Kind = input.kind ?? (Number(rate) === 0 ? 'zero' : 'standard');
    if (['zero', 'exempt', 'outside_scope'].includes(kind) && Number(rate) !== 0) return fail(`A ${kind.replace('_', ' ')} code has a rate of 0%`);
    const bad = checkBoxes(input.boxes);
    if (bad) return fail(bad.message);
    const applies = input.applies_to ?? 'both';
    store.push({ code, name: input.name.trim(), kind, applies_to: applies, is_active: true, rates: one(rate), used_until: null, on_documents: false,
      boxes: input.boxes ?? defaultBoxes(kind, applies, rate) });
    return delay(view());
  },
  updateVatCode: (code, patch: VatCodePatch) => {
    const s = store.find((x) => x.code === code);
    if (!s) return fail(`There’s no VAT code ${code}`);
    if (patch.name !== undefined && !patch.name.trim()) return fail('The VAT code needs a name');
    if (patch.kind && patch.kind !== s.kind && (s.on_documents || s.used_until)) return fail(`${s.code} has been used, so its kind can’t change. Add a new code instead.`);
    const bad = checkBoxes(patch.boxes);
    if (bad) return fail(bad.message);
    Object.assign(s, { ...(patch.name ? { name: patch.name.trim() } : {}), ...(patch.kind ? { kind: patch.kind } : {}),
      ...(patch.applies_to ? { applies_to: patch.applies_to } : {}), ...(patch.boxes ? { boxes: patch.boxes } : {}) });
    return delay(view());
  },
  setVatCodeActive: (code, active) => {
    const s = store.find((x) => x.code === code);
    if (!s) return fail(`There’s no VAT code ${code}`);
    s.is_active = active;
    return delay(view());
  },
  deleteVatCode: (code) => {
    const s = store.find((x) => x.code === code);
    if (!s) return fail(`There’s no VAT code ${code}`);
    if (s.on_documents || s.used_until) return fail(`${s.code} has been used on invoices, bills or journals, so it stays in the books. Switch it off instead.`);
    store = store.filter((x) => x !== s);
    return delay(view());
  },
  setVatRate: (code, raw, from) => {
    const s = store.find((x) => x.code === code);
    if (!s) return fail(`There’s no VAT code ${code}`);
    if (!from) return fail('Say which day the new rate starts');
    const rate = percent(raw);
    if (rate instanceof Error) return fail(rate.message);
    if (['zero', 'exempt', 'outside_scope'].includes(s.kind) && Number(rate) !== 0) return fail(`A ${s.kind.replace('_', ' ')} code has a rate of 0%`);
    if (from <= LAST_FILED) return fail(`The VAT return to ${fmt(LAST_FILED)} has been filed, so a new rate can start on ${fmt(dayAfter(LAST_FILED))} at the earliest`);
    if (s.used_until && from <= s.used_until) {
      return fail(`Entries up to ${fmt(s.used_until)} are posted at the current rate of ${s.code}, so a new rate can start on ${fmt(dayAfter(s.used_until))} at the earliest`);
    }
    const same = s.rates.find((r) => r.from === from);
    if (same) same.rate = rate;
    else {
      const prev = s.rates.filter((r) => r.from < from).sort((a, b) => b.from.localeCompare(a.from))[0];
      const next = s.rates.filter((r) => r.from > from).sort((a, b) => a.from.localeCompare(b.from))[0];
      if (prev) prev.to = from;
      s.rates.push({ rate, from, to: next?.from ?? null });
    }
    return delay(view(DEMO_TODAY, { drafts_to_resave: 0 }));
  },
  removeVatRate: (code, from) => {
    const s = store.find((x) => x.code === code);
    if (!s) return fail(`There’s no VAT code ${code}`);
    const r = s.rates.find((x) => x.from === from);
    if (!r) return fail(`${s.code} has no rate starting ${fmt(from)}`);
    const prev = s.rates.filter((x) => x.from < from).sort((a, b) => b.from.localeCompare(a.from))[0];
    if (!prev) return fail(`That’s the first rate for ${s.code}: a code always has one`);
    if (s.used_until && s.used_until >= from) return fail('Entries from then are posted at that rate, so it stays');
    prev.to = r.to;
    s.rates = s.rates.filter((x) => x !== r);
    return delay(view());
  },
};
